import React, { useState, useEffect } from "react";
import { BASE_URL } from "@/lib/API";
import { DetailsWorkspaceModal } from "../../../components/ui";
import { useProjectActivities } from "../hooks/useProjectActivities";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { fmtDate } from "@/lib/formatters";
import { Caption } from "@/components/ui/Typography";
import {
  User, Mail, Phone, MapPin, Building, Tag, Calendar,
  IndianRupee, Briefcase, Link, Home, Users, CheckCircle2,
  Circle, TrendingUp, CreditCard, Eye, Download, FileText
} from "lucide-react";

const fmt = (v) =>
  v !== undefined && v !== null
    ? new Intl.NumberFormat("en-IN").format(Number(v) || 0)
    : "—";

const InfoField = ({ icon: Icon, label, value, highlight }) => (
  <div className={`flex flex-col gap-0.5 p-3 rounded-lg border ${highlight ? "bg-emerald-50 border-emerald-100" : "bg-slate-50/60 border-slate-100"}`}>
    <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
      {Icon && <Icon size={10} className="shrink-0" />}
      {label}
    </span>
    <span className={`text-[12px] font-semibold ${highlight ? "text-emerald-700" : "text-slate-700"} break-words`}>
      {value || "—"}
    </span>
  </div>
);

const SectionHeader = ({ label, color = "slate" }) => (
  <div className={`text-[9px] font-black uppercase tracking-widest text-${color}-500 flex items-center gap-2 mb-2`}>
    <div className={`w-1.5 h-1.5 rounded-full bg-${color}-400`} />
    {label}
  </div>
);

const MilestoneRow = ({ label, done, progress }) => (
  <div className="flex items-center gap-2">
    {done
      ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
      : <Circle size={14} className="text-slate-300 shrink-0" />}
    <span className={`text-[11px] font-medium capitalize flex-1 ${done ? "text-emerald-700" : "text-slate-500"}`}>{label}</span>
    {progress !== undefined && (
      <span className={`text-[10px] font-bold ${done ? "text-emerald-600" : "text-slate-400"}`}>
        {progress}%
      </span>
    )}
  </div>
);

export const ViewProject = ({
  isOpen, onClose, project, user,
  stages = [], teamMembers = [], projectManagers = [],
  updateStatus, updateProjectState, onToggleDisable
}) => {
  console.log("Project Object:", project);
console.log("Project ID:", project?._id);
  const { activities, addActivity, refreshActivities } = useProjectActivities(project?._id, user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localProject, setLocalProject] = useState(project);

  useEffect(() => {
    if (project) setLocalProject(project);
  }, [project]);

  const role = user?.role?.toLowerCase()?.trim()?.replace(/\s+/g, "-");
  const isSuperAdmin = ["admin", "owner", "Verve-admin"].includes(role);
  const isSalesHead = role === "sales-head" || role === "sale-head";

  const acts = Array.isArray(activities) ? activities : [];

  const handleDetailsNote = async (draftText) => {
    if (!draftText.trim()) return;
    setIsSubmitting(true);
    try {
      await addActivity(draftText, "Note Added");
      refreshActivities();
    } catch (err) { } finally { setIsSubmitting(false); }
  };

  const handleToggleDisable = async () => {
    try {
      await onToggleDisable(localProject);
      setLocalProject((prev) => ({ ...prev, isDisabled: !prev.isDisabled }));
      refreshActivities();
    } catch (err) { showApiError(err); }
  };

  if (!localProject) return null;

  const displayName =
    localProject.customerName ||
    `${localProject.firstName || ""} ${localProject.lastName || ""}`.trim() ||
    "Project";

  // ── Project Info Fields ───────────────────────────────────────
  const projectFields = [
    { icon: User,         label: "Full Name",       value: displayName },
    { icon: Tag,          label: "Project ID",       value: (localProject.customId || "").toUpperCase() },
    { icon: Mail,         label: "Email",            value: localProject.email || "—" },
    { icon: Phone,        label: "Phone",            value: localProject.phoneNumber || localProject.phone || "—" },
    { icon: Briefcase,    label: "Project Status",   value: localProject.projectStatus || "Not started" },
    { icon: Calendar,     label: "Created At",       value: fmtDate(localProject.createdAt) },
    localProject.address1 && { icon: Home,   label: "Address Line 1",   value: localProject.address1 },
    localProject.address2 && { icon: Home,   label: "Address Line 2",   value: localProject.address2 },
    localProject.city     && { icon: MapPin,   label: "City",             value: localProject.city },
    localProject.district && { icon: MapPin,   label: "District",         value: localProject.district },
    localProject.state    && { icon: MapPin,   label: "State",            value: localProject.state },
    localProject.pincode  && { icon: MapPin,   label: "PIN Code",         value: localProject.pincode },
    localProject.officeLocation && { icon: Building, label: "Office",     value: localProject.officeLocation },
    localProject.referenceType  && { icon: Link,     label: "Ref Source",  value: localProject.referenceType },
    localProject.referenceDetails && { icon: Link,   label: "Referral Details", value: localProject.referenceDetails },
    localProject.requirementType && { icon: Building, label: "Property",  value: localProject.requirementType },
    localProject.propertyType   && { icon: Home,     label: "Sub Type",    value: localProject.propertyType },
    (localProject.projectValue || localProject.quotationAmount || localProject.budget) && {
      icon: IndianRupee, label: "Project Value",
      value: `₹${fmt(localProject.projectValue || localProject.quotationAmount || localProject.budget)}`
    },
  ].filter(Boolean);

  // ── Activity Timeline ─────────────────────────────────────────
  const projectTimeline = acts
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
    .map((a, i) => {
      const content = a.note || a.comment || "";
      const text = content.includes(" | Note: ") ? content.split(" | Note: ")[1] : content;
      const action = a.action ? `${a.action}: ` : "";
      const time = a.createdAt ? new Date(a.createdAt).toLocaleString("en-IN") : "";
      return (
        <span key={i} className="flex flex-col gap-0.5">
          <span className="font-bold text-slate-700">{action}{text}</span>
          <span className="text-[9px] text-slate-400">{time} by {a.performedBy?.name || "System"}</span>
        </span>
      );
    });

  // ── Derived project data ───────────────────────────────────────
  const milestones = localProject.installationMilestones || {};
  const tradeList = [
    { key: "carpenter",   label: "Carpenter",   progressKey: "carpenterProgress" },
    { key: "electrician", label: "Electrician", progressKey: "electricianProgress" },
    { key: "painter",     label: "Painter",     progressKey: "painterProgress" },
    { key: "plumber",     label: "Plumber",     progressKey: "plumberProgress" },
  ];

  const pmList = Array.isArray(localProject.projectManager)
    ? localProject.projectManager
    : localProject.projectManager
    ? [localProject.projectManager]
    : [];

  const foList = Array.isArray(localProject.fieldOfficers)
    ? localProject.fieldOfficers
    : [];

  const paymentSummary = localProject.paymentSummary || {};
  const totalPaid = paymentSummary.totalPaid || localProject.advancePaid || 0;
  const projectValue = localProject.projectValue || localProject.quotationAmount || localProject.budget || 0;
  const collectionPct = projectValue > 0 ? Math.min(100, Math.round((totalPaid / projectValue) * 100)) : 0;

  const txCredits  = (paymentSummary.transactions || []).filter(t => t.kind === "credit");
  const txExpenses = (paymentSummary.transactions || []).filter(t => t.kind !== "credit");

  return (
    <>
      <DetailsWorkspaceModal
        isOpen={isOpen}
        onClose={onClose}
        title="Verve Kitchens"
        subtitle="PROJECT INTERACTION DETAILS"
        fields={projectFields}
        timeline={projectTimeline}
        onAddNote={handleDetailsNote}
        isInactive={localProject.isDisabled}
        statusLabel={
          (isSuperAdmin || isSalesHead)
            ? localProject.isDisabled ? "Enable Project" : "Disable Project"
            : null
        }
        statusColor={localProject.isDisabled ? "emerald" : "rose"}
        onStatusToggle={(isSuperAdmin || isSalesHead) ? handleToggleDisable : null}
        saving={isSubmitting}
        savingNote={isSubmitting}
      >
        <div className="flex flex-col gap-5">

          {/* ── Financial Overview ── */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <SectionHeader label="Financial Overview" color="emerald" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <InfoField icon={IndianRupee} label="Project Value"  value={`₹${fmt(projectValue)}`} />
              <InfoField icon={CreditCard}  label="Total Received" value={`₹${fmt(totalPaid)}`} highlight={totalPaid > 0} />
              <InfoField icon={TrendingUp}  label="Target Cost"    value={localProject.targetCost ? `₹${fmt(localProject.targetCost)}` : "—"} />
              <InfoField icon={IndianRupee} label="Net Profit"
                value={projectValue && totalPaid !== undefined ? `₹${fmt(totalPaid - (localProject.targetCost || 0))}` : "—"}
              />
            </div>
            {/* Collection progress bar */}
              <div className="mt-2">
                <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                  <span>Collection Progress</span>
                  <span className="text-emerald-600">{collectionPct}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-700"
                    style={{ width: `${collectionPct}%` }}
                  />
                </div>
              </div>
            </div>

          {/* ── Team Assignment ── */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <SectionHeader label="Team Assignment" color="blue" />
            {pmList.length === 0 && foList.length === 0 ? (
              <span className="text-[11px] text-slate-400 font-medium">Unassigned</span>
            ) : (
              <div className="flex flex-col gap-2">
                {pmList.length > 0 && (
                  <div>
                    <Caption className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Project Manager(s)</Caption>
                    <div className="flex flex-wrap gap-2">
                      {pmList.map((pm, i) => {
                        const name = pm?.name || `${pm?.firstName || ""} ${pm?.lastName || ""}`.trim() || "PM";
                        return (
                          <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
                            <Users size={11} className="text-blue-500 shrink-0" />
                            <span className="text-[11px] font-semibold text-blue-700">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {foList.length > 0 && (
                  <div>
                    <Caption className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-1">Field Officers</Caption>
                    <div className="flex flex-wrap gap-2">
                      {foList.map((fo, i) => {
                        const name = fo?.name || `${fo?.firstName || ""} ${fo?.lastName || ""}`.trim() || "FO";
                        return (
                          <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-50 border border-violet-100 rounded-lg">
                            <User size={11} className="text-violet-500 shrink-0" />
                            <span className="text-[11px] font-semibold text-violet-700">{name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Installation Milestones ── */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <SectionHeader label="Installation Milestones" color="purple" />
            {Object.keys(milestones).length === 0 ? (
              <span className="text-[11px] text-slate-400 font-medium">No milestones set</span>
            ) : (
              <div className="flex flex-col gap-2">
                <MilestoneRow label="Started"    done={!!milestones.started} />
                {tradeList.map(({ key, label, progressKey }) =>
                  milestones[key] !== undefined || milestones[progressKey] !== undefined ? (
                    <MilestoneRow
                      key={key}
                      label={label}
                      done={!!milestones[key]}
                      progress={milestones[progressKey] !== undefined ? milestones[progressKey] : undefined}
                    />
                  ) : null
                )}
              </div>
            )}
          </div>

          {/* ── Payment Transactions Summary ── */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <SectionHeader label="Payment Transactions" color="slate" />
            {txCredits.length === 0 && txExpenses.length === 0 ? (
              <span className="text-[11px] text-slate-400 font-medium">No transactions</span>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Caption className="text-[9px] font-bold uppercase tracking-widest text-emerald-500 mb-1">Credits ({txCredits.length})</Caption>
                  <div className="flex flex-col gap-1">
                    {txCredits.slice(0, 3).map((tx, i) => (
                      <div key={i} className="flex justify-between text-[10px] py-1 border-b border-slate-50">
                        <span className="text-slate-500 truncate max-w-[100px]">
                          {tx.paymentType || tx.categoryLabel || "Credit"}
                        </span>
                        <span className="font-bold text-emerald-600">₹{fmt(tx.amount)}</span>
                      </div>
                    ))}
                    {txCredits.length > 3 && (
                      <span className="text-[9px] text-slate-400">+{txCredits.length - 3} more</span>
                    )}
                  </div>
                </div>
                <div>
                  <Caption className="text-[9px] font-bold uppercase tracking-widest text-rose-500 mb-1">Expenses ({txExpenses.length})</Caption>
                  <div className="flex flex-col gap-1">
                    {txExpenses.slice(0, 3).map((tx, i) => (
                      <div key={i} className="flex justify-between text-[10px] py-1 border-b border-slate-50">
                        <span className="text-slate-500 truncate max-w-[100px]">
                          {tx.expenseCategory || tx.categoryLabel || "Expense"}
                        </span>
                        <span className="font-bold text-rose-600">₹{fmt(tx.amount)}</span>
                      </div>
                    ))}
                    {txExpenses.length > 3 && (
                      <span className="text-[9px] text-slate-400">+{txExpenses.length - 3} more</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Quotation / Documents ── */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <SectionHeader label="Documents" color="indigo" />
            {!localProject.quotationFile?.url && (!localProject.documentUploads || localProject.documentUploads.length === 0) ? (
              <span className="text-[11px] text-slate-400 font-medium">No documents uploaded</span>
            ) : (
              <div className="flex flex-col gap-2">
                {localProject.quotationFile?.url && (
                  <div className="flex items-center gap-2">
                    <FileText size={13} className="text-indigo-500 shrink-0" />
                    <span className="text-[11px] font-medium text-slate-600 flex-1 truncate">Quotation File</span>
                    <a
                      href={`${BASE_URL}${localProject.quotationFile.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded text-[9px] font-bold"
                    >
                      <Eye size={9} /> View
                    </a>
                    <a
                      href={`${BASE_URL}${localProject.quotationFile.url}`}
                      download={localProject.quotationFile.name}
                      className="flex items-center gap-1 px-2 py-1 border border-indigo-200 text-indigo-600 rounded text-[9px] font-bold"
                    >
                      <Download size={9} /> Download
                    </a>
                  </div>
                )}
                {(localProject.documentUploads || []).slice(0, 3).map((doc, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <FileText size={13} className="text-slate-400 shrink-0" />
                    <span className="text-[11px] font-medium text-slate-600 flex-1 truncate">
                      {doc.originalName || `Document ${i + 1}`}
                    </span>
                    <a
                      href={`${BASE_URL}${doc.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1 bg-slate-700 text-white rounded text-[9px] font-bold"
                    >
                      <Eye size={9} /> View
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Appointment ── */}
          <div className="bg-orange-50/40 rounded-xl p-4 border border-orange-100/60">
            <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
              <Calendar size={12} className="text-orange-500" />
              Scheduled Appointment
            </div>
            {localProject.appointmentDate ? (
              <p className="text-[12px] font-semibold text-orange-800">
                {new Date(localProject.appointmentDate).toLocaleDateString("en-IN", {
                  weekday: "long", day: "numeric", month: "long", year: "numeric",
                })}
              </p>
            ) : (
              <span className="text-[11px] text-orange-800 font-medium">Not scheduled</span>
            )}
          </div>

        </div>
      </DetailsWorkspaceModal>
    </>
  );
};
